import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import SkillBrief from "../Views/SkillBrief";
import SkillEditModal from "../Modals/SkillEditModal";
import BreadCrumb from "../Controls/BreadCrumb";
import SkillSearch from "../Controls/SkillSearch";
import EmploymentSelectionListItem from "../ListItems/EmploymentSelectionListItem";
import UserData from "../Controls/UserData";
import { Form, FormSelectList, FormActions } from "../Controls/Form";
import ConfirmDlg from "../Controls/confirm";
import { FaThumbsDown as ThumbsDown, FaThumbsUp as ThumbsUp, FaRegMeh as Meh } from "react-icons/fa";
import { toast } from "react-toastify";
import API from "@src/API";
import { refreshPersonSkillsData, usePersonSkillsQuery } from "../../query/usePersonSkills";
import { refreshEmploymentData, useEmploymentQuery } from "../../query/useEmployment";
import { useConnectionsQuery } from "../../query/useConnections";
import { openSkillForum } from "../../skillForum/openForum";
import CustomButton from "../Controls/CustomButton";
import { personSkillDisplayName } from "../ListItems/PersonSkillListItem";

function linksFromPersonSkill(ps) {
  const links = ps?.EmploymentSkills || ps?.employmentSkills;
  if (Array.isArray(links) && links.length) {
    const map = {};
    for (const link of links) {
      const peid = Number(link.PersonEmploymentID ?? link.personEmploymentID);
      if (!(peid > 0)) continue;
      map[peid] = (link.SkillVersionIDs || link.skillVersionIDs || [])
        .map(Number)
        .filter((n) => n > 0);
    }
    return map;
  }
  return {};
}

const LEVEL_OPTIONS = [
  { value: 6, label: "Beginner (~6 months)", claimedLevel: 1 },
  { value: 18, label: "Junior (~1–2 years)", claimedLevel: 2 },
  { value: 36, label: "Intermediate (~3 years)", claimedLevel: 3 },
  { value: 72, label: "Senior (~6 years)", claimedLevel: 4 },
  { value: 120, label: "Guru (10+ years)", claimedLevel: 5 },
];

/**
 * Person-skill form body (interest, experience, jobs where used).
 * Prefer PersonSkillEditModal for UI entry points.
 */
export default function PersonSkillEdit({
  personSkillId: personSkillIdProp,
  onClose,
  skill: skillProp,
}) {
  const { id: idStr } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isModal = typeof onClose === "function";

  const id =
    personSkillIdProp != null && personSkillIdProp !== undefined
      ? Number.parseInt(personSkillIdProp, 10) || 0
      : idStr && !Number.isNaN(parseInt(idStr, 10))
        ? parseInt(idStr, 10)
        : 0;

  const { data: skillsData } = usePersonSkillsQuery();
  const { data: employmentData } = useEmploymentQuery();
  const { data: connectionsData } = useConnectionsQuery();
  const [personSkill, updatePersonSkill] = useState(() => ({
    ID: 0,
    SkillID: 0,
    InterestLevelID: 1,
    ExperienceMonths: 0,
    Employments: [],
  }));
  const [employmentVersions, setEmploymentVersions] = useState({});
  const [skillVersions, setSkillVersions] = useState([]);
  const [open, openConfirm] = useState(false);
  const [skillModalId, setSkillModalId] = useState(null);
  const [requestVerifierId, setRequestVerifierId] = useState("");
  const [requestBusy, setRequestBusy] = useState(false);

  useEffect(() => {
    if (!(id > 0)) {
      updatePersonSkill({ ID: 0, SkillID: 0, InterestLevelID: 1, ExperienceMonths: 0, Employments: [] });
      setEmploymentVersions({});
      return;
    }
    const found = (skillsData?.personSkills || []).find((ps) => Number(ps.ID || ps.id) === id);
    if (!found) return;
    updatePersonSkill({
      ...found,
      Employments: [...(found.Employments || found.employments || [])],
    });
    setEmploymentVersions(linksFromPersonSkill(found));
  }, [id, skillsData]);

  const catalogSkill = useMemo(() => {
    const skillId = personSkill.SkillID || personSkill.skillID || 0;
    return (
      skillProp ||
      (skillsData?.skills || []).find((s) => Number(s.ID || s.id) === Number(skillId)) ||
      null
    );
  }, [skillProp, skillsData?.skills, personSkill.SkillID, personSkill.skillID]);

  const employments = useMemo(() => {
    const history = employmentData?.employmentHistory || [];
    const release = catalogSkill?.ReleaseDate;
    return history
      .filter((eh) => !release || release < eh.StartDate || !eh.EndDate)
      .sort((a, b) => (a.StartDate < b.StartDate ? 1 : -1));
  }, [employmentData?.employmentHistory, catalogSkill]);

  const breadcrumb = useMemo(() => {
    const base = [{ route: "/skills", label: "Skills" }];
    if (id > 0) {
      const label =
        catalogSkill?.Name ||
        personSkill.SkillName ||
        personSkill.skillName ||
        "Skill";
      return [...base, { route: `/personSkill/${id}`, label }];
    }
    return [...base, { label: "Add Skill" }];
  }, [id, catalogSkill, personSkill.SkillName, personSkill.skillName]);

  function close() {
    if (isModal) onClose?.();
    else navigate("/skills");
  }

  async function refreshUserData() {
    await refreshPersonSkillsData(queryClient);
    await refreshEmploymentData(queryClient);
  }

  const skillIdForVersions = Number(personSkill.SkillID || personSkill.skillID) || 0;

  useEffect(() => {
    let cancelled = false;
    if (!(skillIdForVersions > 0)) {
      setSkillVersions([]);
      return undefined;
    }
    (async () => {
      const resp = await API.getAsync(`/Skill/${skillIdForVersions}/Versions`);
      if (cancelled) return;
      if (resp?.ok && Array.isArray(resp.jsonData)) {
        setSkillVersions(resp.jsonData);
      } else {
        setSkillVersions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skillIdForVersions]);

  const usedVersionLabels = useMemo(() => {
    const ids = new Set();
    Object.values(employmentVersions).forEach((arr) => {
      (arr || []).forEach((vid) => ids.add(Number(vid)));
    });
    return skillVersions
      .filter((v) => ids.has(Number(v.ID ?? v.id)))
      .map((v) => String(v.Version ?? v.version ?? ""))
      .filter(Boolean);
  }, [employmentVersions, skillVersions]);

  async function onDoneAsync() {
    const employmentIds = personSkill.Employments || personSkill.employments || [];
    const payload = {
      ...personSkill,
      Employments: employmentIds,
      EmploymentSkills: employmentIds.map((peid) => ({
        PersonEmploymentID: Number(peid),
        SkillVersionIDs: employmentVersions[Number(peid)] || [],
      })),
    };
    const res = await UserData.UpdateAsync("personSkills", payload, true);
    if (res?.ok) {
      await refreshUserData();
      toast.success("Skill Saved!");
      close();
    } else {
      toast.error(res?.message || "Could not save skill");
    }
  }

  function onSetInterestLevel(value) {
    updatePersonSkill({ ...personSkill, InterestLevelID: value });
  }

  function isEmploymentSelected(employmentId) {
    const list = personSkill.Employments || personSkill.employments || [];
    return list.some((e) => Number(e) === Number(employmentId));
  }

  async function onToggleEmployment(employmentId) {
    const skillId = personSkill.SkillID || personSkill.skillID;
    if (!skillId) return;
    const url = `PersonEmployment/${employmentId}/SkillToggle/${skillId}`;
    const resp = await API.putAsync(url);
    if (!resp?.ok) return;

    const localEmployments = [...(personSkill.Employments || [])];
    const index = localEmployments.findIndex((eid) => Number(eid) === Number(employmentId));
    const peid = Number(employmentId);
    if (index === -1) {
      localEmployments.push(employmentId);
      setEmploymentVersions((prev) => ({ ...prev, [peid]: prev[peid] || [] }));
    } else {
      localEmployments.splice(index, 1);
      setEmploymentVersions((prev) => {
        const next = { ...prev };
        delete next[peid];
        return next;
      });
    }
    updatePersonSkill({ ...personSkill, Employments: localEmployments });
  }

  async function onToggleVersion(employmentId, versionId) {
    const skillId = personSkill.SkillID || personSkill.skillID;
    if (!skillId) return;
    const peid = Number(employmentId);
    const vid = Number(versionId);
    const current = [...(employmentVersions[peid] || [])];
    const idx = current.indexOf(vid);
    if (idx === -1) current.push(vid);
    else current.splice(idx, 1);

    const resp = await API.putAsync(`PersonEmployment/${peid}/Skill/${skillId}`, {
      SkillVersionIDs: current,
    });
    if (!resp?.ok) {
      toast.error(resp?.jsonData?.message || "Could not update versions");
      return;
    }
    setEmploymentVersions((prev) => ({ ...prev, [peid]: current }));
    if (!isEmploymentSelected(peid)) {
      updatePersonSkill({
        ...personSkill,
        Employments: [...(personSkill.Employments || []), peid],
      });
    }
  }

  function onToggleAllEmployments() {
    const selected = personSkill.Employments || [];
    const allSelected =
      employments.length > 0 &&
      employments.length === selected.length &&
      employments.every((emp) => selected.includes(emp.ID));
    updatePersonSkill({
      ...personSkill,
      Employments: allSelected ? [] : employments.map((emp) => emp.ID),
    });
    if (allSelected) setEmploymentVersions({});
  }

  async function onDlgResponse(value) {
    openConfirm(false);
    if (String(value).toLowerCase() !== "delete") return;

    const personSkillRowId = personSkill.ID || personSkill.id;
    if (!(personSkillRowId > 0)) return;

    const result = await UserData.DeleteAsync("personSkills", personSkillRowId);
    if (result?.result) {
      toast.success("Skill deleted!");
      await refreshUserData();
      close();
    } else {
      toast.error(result?.error || "Could not delete skill");
    }
  }

  async function onDelete() {
    openConfirm(true);
  }

  const skillName =
    personSkill.SkillName || personSkill.skillName || catalogSkill?.Name || "this skill";
  const months = Number(personSkill.ExperienceMonths) || 0;
  const levelOptions = LEVEL_OPTIONS.some((o) => o.value === months)
    ? LEVEL_OPTIONS
    : months > 0
      ? [{ value: months, label: `${months} months (current)` }, ...LEVEL_OPTIONS]
      : [{ value: 0, label: "Experience level…" }, ...LEVEL_OPTIONS];

  const formBody = (
    <Form
      className={isModal ? "cm-modal-form" : undefined}
      title="PersonSkill"
      object={personSkill}
      onUpdate={updatePersonSkill}
    >
      <div className="field">
        <label>Experience level with {skillName}</label>
        <select
          value={months}
          onChange={(e) => {
            const nextMonths = Number(e.target.value) || 0;
            const band = LEVEL_OPTIONS.find((o) => o.value === nextMonths);
            updatePersonSkill({
              ...personSkill,
              ExperienceMonths: nextMonths,
              ClaimedSkillLevel: band?.claimedLevel ?? personSkill.ClaimedSkillLevel,
            });
          }}
        >
          {levelOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>Please select your level of interest in continuing to use {skillName} in the future</label>
        <ul className="interestLevel">
          <li key="1">
            <button
              type="button"
              className={personSkill.InterestLevelID === 1 ? "is-selected" : ""}
              aria-pressed={personSkill.InterestLevelID === 1}
              title="Yes - I'd like to do more of this"
              onClick={() => onSetInterestLevel(1)}
            >
              <ThumbsUp
                className={`interestLevelIcon up ${personSkill.InterestLevelID === 1 ? "selected" : ""}`}
                size={24}
              />
              <span>More</span>
            </button>
          </li>
          <li key="0">
            <button
              type="button"
              className={personSkill.InterestLevelID === 0 ? "is-selected" : ""}
              aria-pressed={personSkill.InterestLevelID === 0}
              title="Meh - Don't care if I use this skill again or not"
              onClick={() => onSetInterestLevel(0)}
            >
              <Meh
                className={`interestLevelIcon meh ${personSkill.InterestLevelID === 0 ? "selected" : ""}`}
                size={24}
              />
              <span>Neutral</span>
            </button>
          </li>
          <li key="2">
            <button
              type="button"
              className={personSkill.InterestLevelID === 2 ? "is-selected" : ""}
              aria-pressed={personSkill.InterestLevelID === 2}
              title="No - I'd rather not use this again"
              onClick={() => onSetInterestLevel(2)}
            >
              <ThumbsDown
                className={`interestLevelIcon down ${personSkill.InterestLevelID === 2 ? "selected" : ""}`}
                size={24}
              />
              <span>Less</span>
            </button>
          </li>
        </ul>
      </div>

      <FormSelectList
        field="Employments"
        label={`Select the jobs where you have used this skill: (${(personSkill.Employments || []).length} selected of ${employments.length})`}
        options={employments}
        onToggleSelect={() => onToggleAllEmployments()}
        renderItem={(employment) => (
          <EmploymentSelectionListItem
            key={employment.ID}
            employment={employment}
            employers={employmentData?.employers || []}
            selected={isEmploymentSelected(employment.ID)}
            onClick={onToggleEmployment}
            versionOptions={skillVersions}
            selectedVersionIds={employmentVersions[Number(employment.ID)] || []}
            onToggleVersion={onToggleVersion}
          />
        )}
      />

      {Number(personSkill.SkillID || personSkill.skillID) > 0 &&
      (personSkill.ID || personSkill.id) > 0 ? (
        <div className="field form-group" data-testid="request-skill-endorsement">
          <label htmlFor="request-endorsement-verifier">Request coworker corroboration</label>
          <p className="fieldHint">
            Email a verified coworker to endorse this skill claim (raises claim trust; does not set
            verified level).
          </p>
          <select
            id="request-endorsement-verifier"
            value={requestVerifierId}
            onChange={(e) => setRequestVerifierId(e.target.value)}
          >
            <option value="">Select verified coworker…</option>
            {(connectionsData?.connections || [])
              .filter((c) => c.CoworkerVerified || c.coworkerVerified)
              .map((c) => {
                const pid = c.PersonID ?? c.personID;
                return (
                  <option key={pid} value={pid}>
                    {(c.FirstName || c.firstName || "") + " " + (c.LastName || c.lastName || "")}
                  </option>
                );
              })}
          </select>
          <CustomButton
            type="button"
            variant="contained"
            color="primary"
            disabled={requestBusy || !requestVerifierId}
            data-testid="request-endorsement-send"
            onClick={async () => {
              const verifierPersonId = Number(requestVerifierId);
              const skillId = Number(personSkill.SkillID || personSkill.skillID);
              if (!(verifierPersonId > 0) || !(skillId > 0)) return;
              setRequestBusy(true);
              try {
                const resp = await API.postAsync("/SkillEndorsement/Request", {
                  verifierPersonId,
                  skillId,
                });
                if (resp?.ok) toast.success("Corroboration request sent");
                else toast.error(resp?.jsonData?.message || resp?.message || "Request failed");
              } finally {
                setRequestBusy(false);
              }
            }}
          >
            {requestBusy ? "Sending…" : "Send request"}
          </CustomButton>
        </div>
      ) : null}

      <FormActions
        onSaveAsync={async () => {
          await onDoneAsync();
        }}
        onCancelAsync={async () => {
          close();
        }}
        onDeleteAsync={
          (personSkill.ID || personSkill.id || 0) > 0
            ? async () => {
                await onDelete();
              }
            : undefined
        }
      />
    </Form>
  );

  if (isModal) {
    return (
      <div className="cm-modal-embedded person-skill-editor">
        {id === 0 ? (
          <SkillSearch
            label="Search for a skill"
            onSelect={(selected) => {
              updatePersonSkill({
                ...personSkill,
                SkillID: selected.ID || selected.id,
                SkillName: selected.Name || selected.name,
              });
            }}
          />
        ) : (
          <div className="pageSection person-skill-editor__summary">
            <SkillBrief
              skillID={personSkill.SkillID || personSkill.skillID}
              skill={catalogSkill}
              usedVersions={usedVersionLabels}
              onEdit={(catalogId) => setSkillModalId(catalogId)}
            />
            {Number(personSkill.SkillID || personSkill.skillID) > 0 ? (
              <CustomButton
                type="button"
                variant="contained"
                color="primary"
                data-testid="person-skill-open-forum"
                onClick={() => {
                  const forumSkillId = Number(personSkill.SkillID || personSkill.skillID);
                  const forumName = personSkillDisplayName(catalogSkill, personSkill) || skillName;
                  openSkillForum({ skillId: forumSkillId, skillName: forumName });
                  if (typeof onClose === "function") onClose();
                  navigate(`/skillForum/${forumSkillId}`);
                }}
              >
                Open Skill Forum
              </CustomButton>
            ) : null}
          </div>
        )}
        {formBody}
        <ConfirmDlg title="Delete Skill?" open={open} onResponse={onDlgResponse} delete cancel>
          <p>
            WARNING: This will delete all occurrences of this skill on previous jobs and resumes. Are
            you sure you want to continue?
          </p>
        </ConfirmDlg>
        {skillModalId != null ? (
          <SkillEditModal
            skillId={skillModalId}
            onClose={async (saved) => {
              setSkillModalId(null);
              if (saved) await refreshPersonSkillsData(queryClient);
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <BreadCrumb props={breadcrumb} />
      {id === 0 ? (
        <SkillSearch
          title="Edit Skill"
          skill={personSkill}
          onSelect={(selected) => {
            updatePersonSkill({ ...personSkill, SkillID: selected.ID });
          }}
        />
      ) : (
        <>
          <div className="tabbody">
            <h1>Skill - {skillName}</h1>
            <div className="pageSection">
              <SkillBrief
                skillID={personSkill.SkillID}
                skill={catalogSkill}
                usedVersions={usedVersionLabels}
                onEdit={(catalogId) => setSkillModalId(catalogId)}
              />
            </div>
            {formBody}
          </div>
          <ConfirmDlg title="Delete Skill?" open={open} onResponse={onDlgResponse} delete cancel>
            <p>
              WARNING: This will delete all occurrences of this skill on previous jobs and resumes. Are
              you sure you want to continue?
            </p>
          </ConfirmDlg>
          {skillModalId != null ? (
            <SkillEditModal
              skillId={skillModalId}
              onClose={async (saved) => {
                setSkillModalId(null);
                if (saved) await refreshPersonSkillsData(queryClient);
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
